// Reading the Authentication-Results header our own mail server adds on arrival (RFC 8601): did SPF, DKIM and DMARC pass?

export type AuthVerdict = "pass" | "fail" | "softfail" | "neutral" | "none" | "temperror" | "permerror" | "policy";

export interface AuthSummary {
    spf?: AuthVerdict;
    dkim?: AuthVerdict;
    dmarc?: AuthVerdict;
    /** "verified": DMARC passed (or SPF and DKIM both did); "failed": something failed; "unknown": no usable result. */
    overall: "verified" | "failed" | "unknown";
}

const KNOWN = new Set<AuthVerdict>(["pass", "fail", "softfail", "neutral", "none", "temperror", "permerror", "policy"]);

function verdictOf(header: string, method: "spf" | "dkim" | "dmarc"): AuthVerdict | undefined {
    const m = new RegExp(`(?:^|[\\s;])${method}\\s*=\\s*([a-z]+)`, "i").exec(header);
    const v = m?.[1]?.toLowerCase() as AuthVerdict | undefined;
    return v && KNOWN.has(v) ? v : undefined;
}

export function parseAuthResults(header: string | null | undefined): AuthSummary {
    if (!header) return { overall: "unknown" };
    const spf = verdictOf(header, "spf");
    const dkim = verdictOf(header, "dkim");
    const dmarc = verdictOf(header, "dmarc");
    const bad = (v?: AuthVerdict) => v === "fail" || v === "softfail" || v === "permerror";
    let overall: AuthSummary["overall"] = "unknown";
    if (bad(dmarc) || (dmarc === undefined && (bad(spf) || bad(dkim)))) overall = "failed";
    else if (dmarc === "pass" || (spf === "pass" && dkim === "pass")) overall = "verified";
    return { spf, dkim, dmarc, overall };
}

export const verdictLabel = (v?: AuthVerdict): string => (v ? v.replace("softfail", "soft fail") : "not checked");
